/**
 * Utilities for In-depth analysis structured editing.
 * Converts between JSON / markdown storage and plain-text section fields.
 */

const SECTION_DEFS = [
  { key: 'agreements', header: 'Agreements' },
  { key: 'differences', header: 'Differences' },
  { key: 'partialAgreements', header: 'Partial agreements' },
  { key: 'takeaways', header: 'Takeaways' },
  { key: 'thoughtProvoking', header: 'Thought Provoking Comments' },
  { key: 'followUpQuestions', header: 'Follow-up Questions' },
];

export const IN_DEPTH_SECTION_LABELS = SECTION_DEFS.map(({ key, header }) => ({ key, label: header }));

function emptyInDepthSections() {
  return Object.fromEntries(SECTION_DEFS.map(({ key }) => [key, '']));
}

function formatSpeakers(speakers) {
  if (!speakers) return '';
  if (Array.isArray(speakers)) return speakers.join(', ');
  return String(speakers);
}

function appendField(lines, label, value) {
  if (!value) return;
  if (label === 'Speakers' || label === 'Speaker') {
    lines.push(`${label}: ${formatSpeakers(value)}`);
  } else {
    lines.push(`${label}: ${typeof value === 'string' ? value.trim() : String(value)}`);
  }
}

function dictItemsToPlainText(items, fields) {
  if (!items || typeof items !== 'object') return '';
  const blocks = [];
  for (const [title, item] of Object.entries(items)) {
    if (!item || typeof item !== 'object') continue;
    const lines = [title];
    for (const [label, key] of fields) {
      appendField(lines, label, item[key]);
    }
    blocks.push(lines.join('\n'));
  }
  return blocks.join('\n\n');
}

function overallAssessmentToPlainText(assessment) {
  if (!assessment || typeof assessment !== 'object') return '';
  const lines = [];
  if (assessment.Summary) lines.push(assessment.Summary);
  if (assessment['Consensus level']) lines.push(`Consensus level: ${assessment['Consensus level']}`);
  if (assessment['Disagreement level']) lines.push(`Disagreement level: ${assessment['Disagreement level']}`);
  return lines.join('\n');
}

function agreementsToPlainText(agreements) {
  if (!agreements || typeof agreements !== 'object') return '';
  const parts = [];

  const agreementPoints = dictItemsToPlainText(agreements['Agreement points'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Summary', 'Summary'],
    ['Policy Context', 'Policy_context'],
  ]);
  if (agreementPoints) parts.push(`Agreement points\n\n${agreementPoints}`);

  const similar = dictItemsToPlainText(agreements['Similar viewpoints'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Policy Context', 'Policy_context'],
  ]);
  if (similar) parts.push(`Similar viewpoints\n\n${similar}`);

  const unexpected = dictItemsToPlainText(agreements['Unexpected consensus'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Explanation', 'Explanation'],
    ['Policy Context', 'Policy_context'],
  ]);
  if (unexpected) parts.push(`Unexpected consensus\n\n${unexpected}`);

  const overall = overallAssessmentToPlainText(agreements['Overall assessment']);
  if (overall) parts.push(`Overall assessment\n\n${overall}`);

  return parts.join('\n\n');
}

function differencesToPlainText(differences) {
  if (!differences || typeof differences !== 'object') return '';
  const parts = [];

  const different = dictItemsToPlainText(differences['Different viewpoints'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Summary', 'Summary'],
    ['Policy Context', 'Policy_context'],
  ]);
  if (different) parts.push(`Different viewpoints\n\n${different}`);

  const unexpected = dictItemsToPlainText(differences['Unexpected differences'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Explanation', 'Explanation'],
    ['Policy Context', 'Policy_context'],
  ]);
  if (unexpected) parts.push(`Unexpected differences\n\n${unexpected}`);

  const overall = overallAssessmentToPlainText(differences['Overall assessment']);
  if (overall) parts.push(`Overall assessment\n\n${overall}`);

  return parts.join('\n\n');
}

function partialAgreementsToPlainText(partialAgreements) {
  if (!partialAgreements || typeof partialAgreements !== 'object') return '';
  return dictItemsToPlainText(partialAgreements['Partial agreements'], [
    ['Speakers', 'Speakers'],
    ['Arguments', 'Arguments'],
    ['Summary', 'Summary'],
  ]);
}

function takeawaysToPlainText(takeaways) {
  if (!takeaways || typeof takeaways !== 'object') return '';
  const parts = [];
  const fields = [
    ['Key takeaways', 'Key takeaways'],
    ['Resolutions and action items', 'Resolutions and action items'],
    ['Unresolved issues', 'Unresolved issues'],
    ['Suggested compromises', 'Suggested compromises'],
  ];
  for (const [label, key] of fields) {
    const val = takeaways[key];
    if (val) parts.push(`${label}\n${typeof val === 'string' ? val : String(val)}`);
  }
  return parts.join('\n\n');
}

function thoughtProvokingToPlainText(comments) {
  if (!comments || typeof comments !== 'object') return '';
  const blocks = [];
  for (const [title, comment] of Object.entries(comments)) {
    if (title === 'Overall assessment') continue;
    if (!comment || typeof comment !== 'object') continue;
    const lines = [`"${title}"`];
    appendField(lines, 'Speaker', comment.Speaker);
    appendField(lines, 'Reason', comment.Reason);
    appendField(lines, 'Impact', comment.Impact);
    blocks.push(lines.join('\n'));
  }
  if (comments['Overall assessment']) {
    blocks.push(`Overall assessment\n${comments['Overall assessment']}`);
  }
  return blocks.join('\n\n');
}

function followUpToPlainText(questions) {
  if (!questions || typeof questions !== 'object') return '';
  const blocks = [];
  for (const [title, question] of Object.entries(questions)) {
    if (!question || typeof question !== 'object') continue;
    const lines = [title];
    appendField(lines, 'Speaker', question.Speaker);
    appendField(lines, 'Explanation', question.Explanation);
    blocks.push(lines.join('\n'));
  }
  return blocks.join('\n\n');
}

/**
 * Convert Additional analysis JSON to plain section fields.
 */
function additionalAnalysisToSections(additionalAnalysis) {
  if (!additionalAnalysis || typeof additionalAnalysis !== 'object') {
    return emptyInDepthSections();
  }
  return {
    agreements: agreementsToPlainText(additionalAnalysis.Agreements),
    differences: differencesToPlainText(additionalAnalysis.Differences),
    partialAgreements: partialAgreementsToPlainText(additionalAnalysis['Partial agreements']),
    takeaways: takeawaysToPlainText(additionalAnalysis.Takeaways),
    thoughtProvoking: thoughtProvokingToPlainText(additionalAnalysis['Thought provoking comments']),
    followUpQuestions: followUpToPlainText(additionalAnalysis['Follow-up questions']),
  };
}

/**
 * Strip common markdown syntax for display in plain textareas.
 */
function stripMarkdownSyntax(text) {
  if (!text) return '';
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^\*\s+/gm, '- ')
    .replace(/^>\s+/gm, '')
    .trim();
}

/**
 * Parse stored in_depth_md markdown into plain section fields.
 */
export function parseInDepthMarkdown(markdown) {
  const sections = emptyInDepthSections();
  if (!markdown || !markdown.trim()) return sections;

  let currentKey = null;
  const buffers = Object.fromEntries(SECTION_DEFS.map(({ key }) => [key, []]));

  for (const line of markdown.split('\n')) {
    const trimmed = line.trim();
    const matched = SECTION_DEFS.find(({ header }) => trimmed === `## ${header}`);
    if (matched) {
      currentKey = matched.key;
      continue;
    }
    if (currentKey) {
      buffers[currentKey].push(line);
    }
  }

  for (const { key } of SECTION_DEFS) {
    sections[key] = stripMarkdownSyntax(buffers[key].join('\n'));
  }

  return sections;
}

/**
 * Combine plain section fields back to markdown for storage.
 */
export function combineInDepthSections(sections) {
  const parts = [];
  for (const { key, header } of SECTION_DEFS) {
    const content = (sections[key] || '').trim();
    if (content) {
      parts.push(`## ${header}\n\n${content}`);
    }
  }
  return parts.join('\n\n');
}

/**
 * Resolve initial sections from JSON and/or markdown.
 */
export function resolveInDepthSections({ additionalAnalysis, inDepthMd, preferJson = false }) {
  const fromJson = additionalAnalysis
    ? additionalAnalysisToSections(additionalAnalysis)
    : emptyInDepthSections();
  const fromMarkdown = parseInDepthMarkdown(inDepthMd);

  const jsonHasContent = Object.values(fromJson).some((v) => v.trim());
  const mdHasContent = Object.values(fromMarkdown).some((v) => v.trim());

  if (preferJson && jsonHasContent) return fromJson;
  if (mdHasContent) return fromMarkdown;
  if (jsonHasContent) return fromJson;
  return emptyInDepthSections();
}

export function sectionsHaveContent(sections) {
  return Object.values(sections || {}).some((v) => (v || '').trim());
}

export function isStubInDepthMarkdown(markdown) {
  if (!markdown || !markdown.trim()) return true;
  const parsed = parseInDepthMarkdown(markdown);
  return !sectionsHaveContent(parsed);
}

/**
 * Resolve the structured "Additional analysis" dict for the structured editor.
 *
 * The session field `in_depth_md` is the source of truth: for generated /
 * translated / structure-preserving edits it holds a JSON dict string, while
 * legacy editor saves hold flat markdown. Returns the dict when we have one
 * (so the editor can keep the nested structure that DigWatch renders), or
 * null when the content is legacy markdown / empty (fall back to the flat
 * markdown editor so no data is lost).
 */
export function resolveAnalysisDict({ additionalAnalysis, inDepthMd } = {}) {
  const isNonEmptyObject = (v) =>
    v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length > 0;

  if (typeof inDepthMd === 'string' && inDepthMd.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(inDepthMd.trim());
      if (isNonEmptyObject(parsed)) return parsed;
    } catch (e) {
      // Not JSON — treat as legacy markdown.
    }
  } else if (isNonEmptyObject(inDepthMd)) {
    return inDepthMd;
  }

  if (isNonEmptyObject(additionalAnalysis)) return additionalAnalysis;

  return null;
}

/**
 * Recursively drop empty leaves (blank strings, empty arrays/objects) so the
 * saved dict mirrors what report generation produces and DigWatch does not
 * render empty labels. Returns undefined when the whole node becomes empty.
 */
export function pruneEmptyAnalysis(value) {
  if (typeof value === 'string') {
    return value.trim() ? value : undefined;
  }
  if (Array.isArray(value)) {
    const cleaned = value
      .map((item) => pruneEmptyAnalysis(item))
      .filter((item) => item !== undefined);
    return cleaned.length ? cleaned : undefined;
  }
  if (value && typeof value === 'object') {
    const out = {};
    for (const [key, val] of Object.entries(value)) {
      const cleaned = pruneEmptyAnalysis(val);
      if (cleaned !== undefined) out[key] = cleaned;
    }
    return Object.keys(out).length ? out : undefined;
  }
  // numbers / booleans / null — keep as-is (null dropped)
  return value === null ? undefined : value;
}
